import { useState } from 'react';
import { Button, Dialog, Field, Icon, Select, useErrorDialog, useToast } from '@dayon/design-system';
import { formatCount } from '@dayon/money';
import { api, ApiRequestError } from '../../../lib/api.ts';
import { TIERS, TIER_LABEL, TIER_REASONS } from '../vocabulary.ts';
import type { TierOutcome } from '../types.ts';

/**
 * Move a selection's tier.
 *
 * A COMMERCIAL decision rather than a control, and it is treated as one: its
 * own capability (`players.tier`, which a Risk Officer does not hold), its own
 * closed set of reasons, and a note — because "why is this player VIP 4" is a
 * question a commercial review asks six months later and a code alone reads as
 * a form number.
 *
 * The move is recorded on player_status_history under the TIER dimension, with
 * the tier each player came FROM. `player.tier` on its own would say what
 * somebody is today and nothing about who moved them or when.
 */
export function TierDialog({
  open,
  onClose,
  playerIds,
  currentTier,
  subject,
  onDone,
}: {
  open: boolean;
  onClose: () => void;
  playerIds: string[];
  /** Whose account it is, when there is exactly one. */
  subject?: string | undefined;
  /** The tier of the one player being edited, when there is only one. */
  currentTier?: string | undefined;
  onDone: () => void;
}) {
  const { showError } = useErrorDialog();
  const { toast } = useToast();
  const [tier, setTier] = useState<string>(TIERS[0]);
  const [reason, setReason] = useState<string>(TIER_REASONS[0].code);
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);

  const count = playerIds.length;
  const noteError =
    note.length > 0 && note.trim().length < 10
      ? 'At least 10 characters — a code on its own reads as a form number.'
      : undefined;
  /* Refused in the form as well as at the route: a submission where every
     player is already on the chosen tier is a 409, and meeting that as a modal
     after pressing the button is worse than meeting it as a disabled one. */
  const noChange = count === 1 && currentTier !== undefined && currentTier === tier;
  const ready = count > 0 && note.trim().length >= 10 && !noChange;

  const submit = async () => {
    if (!ready || saving) return;
    setSaving(true);
    try {
      const result = await api.post<TierOutcome>('/api/players/tier', {
        playerIds,
        tier,
        reasonCode: reason,
        note: note.trim(),
      });
      toast(
        `${TIER_LABEL[tier] ?? tier} set on ${formatCount(result.changed.length)} player` +
          `${result.changed.length === 1 ? '' : 's'}` +
          (result.skipped.length > 0
            ? ` · ${formatCount(result.skipped.length)} already on it`
            : '') +
          ' · written to the audit trail',
      );
      setNote('');
      onClose();
      onDone();
    } catch (error: unknown) {
      showError({
        title: `Could not set the tier on ${formatCount(count)} player${count === 1 ? '' : 's'}`,
        message:
          error instanceof ApiRequestError
            ? error.message
            : 'Nothing was written. The whole submission is one transaction, so no part of it applied.',
        ...(error instanceof ApiRequestError ? { code: error.code } : {}),
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={
        subject && count === 1
          ? `Set the tier on ${subject}?`
          : `Set the tier on ${formatCount(count)} player${count === 1 ? '' : 's'}?`
      }
      wide
      dismissible={false}
      actions={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="primary"
            disabled={!ready || saving}
            aria-describedby="players-tier-gap"
            onClick={() => void submit()}
          >
            {saving ? 'Applying…' : `Set ${TIER_LABEL[tier] ?? tier}`}
          </Button>
        </>
      }
    >
      <p className="text-muted" style={{ fontSize: '13px' }}>
        Every player in the selection is moved to the same tier, with the same reason and the same
        note. Anyone already on it is skipped by name rather than given a second history row.
      </p>

      <div className="stack">
        <Field
          label="Tier"
          error={noChange ? 'That is already this player’s tier — nothing would change.' : undefined}
          hint="The five tiers a player can be on. There is no “Watch” tier on this platform, so it is not offered."
        >
          {({ id, describedBy, invalid }) => (
            <Select
              id={id}
              aria-describedby={describedBy}
              aria-invalid={invalid || undefined}
              value={tier}
              onChange={(e) => setTier(e.target.value)}
            >
              {TIERS.map((t) => (
                <option key={t} value={t}>
                  {TIER_LABEL[t] ?? t}
                  {currentTier === t ? ' — current' : ''}
                </option>
              ))}
            </Select>
          )}
        </Field>

        <Field
          label="Reason"
          hint="Stored on the history row. It is what a commercial review groups by: “how many did we downgrade for inactivity this quarter” cannot be answered from prose."
        >
          {({ id, describedBy }) => (
            <Select
              id={id}
              aria-describedby={describedBy}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            >
              {TIER_REASONS.map((r) => (
                <option key={r.code} value={r.code}>
                  {r.label}
                </option>
              ))}
            </Select>
          )}
        </Field>

        <Field
          label="Note"
          error={noteError}
          hint="At least 10 characters. A tier change moves what a player is offered and what their agent is paid on them, so it says who decided and against what."
        >
          {({ id, describedBy, invalid }) => (
            <textarea
              id={id}
              className="input"
              rows={3}
              aria-describedby={describedBy}
              aria-invalid={invalid || undefined}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Q3 VIP review — cleared the ₱2m lifetime threshold two months running."
            />
          )}
        </Field>

        <div className="notice notice-quiet" id="players-tier-gap">
          <Icon name="info" />
          <div>
            <strong>What this writes.</strong> a player’s tier,{' '}
            {/* The history row's dimension is TIER. No tier-to-benefit, -limit or -multiplier
                table exists. The KYC rejection notice stopped on 23 Oct 2026; the identity
                queue's decision (IDENTITY_RESULT) is what goes through the outbox now. */}
            one status-history row per player recording the tier they came from, and one audit
            entry each — all saved together. <strong>Nothing else moves with it.</strong> Nothing
            on this platform links a tier to a benefit, a limit or a points multiplier, so a tier
            here is a label the programme is run from by hand: no withdrawal ceiling is raised,
            no points multiplier changes and no bonus is granted by this write. And the player is
            not told — the outbox would take it, and an identity check&rsquo;s decision goes out
            through it, but this
            change records nothing there and no template carries a tier move.
          </div>
        </div>
      </div>
    </Dialog>
  );
}
